import React from 'react'
import { Alert } from 'antd'
import { useIntl } from 'react-intl'
import { 
  ExclamationCircleOutlined, 
  WarningOutlined, 
  InfoCircleOutlined 
} from '@ant-design/icons'

export interface AlertBannerItem {
  id: string
  type: 'error' | 'warning' | 'info'
  title: string
  description?: string
  action?: {
    label: string
    onClick: () => void
  }
  closable?: boolean
  onClose?: () => void
}

interface AlertBannerProps {
  alerts: AlertBannerItem[]
  maxAlerts?: number
  className?: string
}

const AlertBannerComponent: React.FC<AlertBannerProps> = ({
  alerts,
  maxAlerts = 3,
  className = '',
}) => {
  const { formatMessage } = useIntl()

  const getIcon = (type: string) => {
    switch (type) {
      case 'error': return <ExclamationCircleOutlined />
      case 'warning': return <WarningOutlined />
      case 'info': return <InfoCircleOutlined />
      default: return <InfoCircleOutlined />
    }
  }

  const displayAlerts = alerts.slice(0, maxAlerts)

  if (displayAlerts.length === 0) {
    return null
  }

  return (
    <div className={`alert-banner ${className}`} style={{ marginBottom: 16 }}>
      {displayAlerts.map((alert) => (
        <Alert
          key={alert.id}
          type={alert.type}
          showIcon
          icon={getIcon(alert.type)}
          message={alert.description || alert.title}
          description={undefined}
          closable={alert.closable}
          onClose={alert.onClose}
          style={{ 
            marginBottom: 8, 
            padding: '8px 12px',
            minHeight: 'auto',
          }}
        />
      ))}
    </div>
  )
}

// Memoize component to prevent unnecessary re-renders
// Only re-render when alerts array changes
const AlertBanner = React.memo(AlertBannerComponent, (prevProps, nextProps) => {
  // Custom comparison function - return true if props are equal (skip re-render)
  return (
    // Check array reference - assumes immutable updates
    prevProps.alerts === nextProps.alerts &&
    prevProps.maxAlerts === nextProps.maxAlerts &&
    prevProps.className === nextProps.className
  )
})

// Set display name for debugging
AlertBanner.displayName = 'AlertBanner'

export default AlertBanner